// Service de gestion des notifications push
import { messaging } from './firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Clé VAPID (à générer depuis Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = 'BNPD34zOXwB2ywHMg6U0h8FvnV0dqV5dvsGH_Fq_vxWEHM4PlMTgcTSvZBW0OrY9wabPzezieKSLaEWvObvnVi0';

/**
 * Demander la permission pour les notifications push
 * @param {string} userId - ID de l'utilisateur Firebase
 * @param {string} companyId - ID de l'entreprise
 * @returns {Promise<boolean>} true si la permission est accordée
 */
export async function requestNotificationPermission(userId, companyId) {
  if (!messaging) {
    console.warn('Notifications push non disponibles (nécessite HTTPS et service worker)');
    return false;
  }

  try {
    // Demander la permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('✅ Permission de notification accordée');
      
      // Récupérer le token FCM
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY
      });
      
      if (token) {
        console.log('📱 Token FCM obtenu:', token.substring(0, 20) + '...');
        
        // Sauvegarder le token dans Firestore
        await saveNotificationToken(userId, companyId, token);
        
        return true;
      } else {
        console.warn('⚠️ Impossible d\'obtenir le token FCM');
        return false;
      }
    } else if (permission === 'denied') {
      console.warn('❌ Permission de notification refusée');
      return false;
    } else {
      console.log('⏸️ Permission de notification en attente');
      return false;
    }
  } catch (error) {
    console.error('Erreur demande permission notifications:', error);
    return false;
  }
}

/**
 * Sauvegarder le token FCM dans Firestore
 */
async function saveNotificationToken(userId, companyId, token) {
  try {
    const tokenRef = doc(db, 'users', userId);
    
    // Récupérer les tokens existants
    const userDoc = await getDoc(tokenRef);
    const userData = userDoc.data() || {};
    const existingTokens = userData.fcmTokens || [];
    
    // Ajouter le nouveau token s'il n'existe pas déjà
    if (!existingTokens.includes(token)) {
      existingTokens.push(token);
      
      await setDoc(tokenRef, {
        fcmTokens: existingTokens,
        lastTokenUpdate: new Date(),
        companyId: companyId
      }, { merge: true });
      
      console.log('💾 Token FCM sauvegardé');
    }
  } catch (error) {
    console.error('Erreur sauvegarde token FCM:', error);
  }
}

/**
 * Écouter les notifications en foreground (quand l'app est ouverte)
 * @param {Function} callback - Fonction appelée quand une notification arrive
 */
export function onForegroundMessage(callback) {
  if (!messaging) return;
  
  onMessage(messaging, (payload) => {
    console.log('📬 Notification reçue (foreground):', payload);
    
    // Afficher une notification personnalisée
    if (payload.notification) {
      const { title, body, icon } = payload.notification;
      
      // Créer une notification native
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: icon || '/logo192.png',
          badge: '/logo192.png',
          tag: payload.data?.type || 'default',
          requireInteraction: false,
          data: payload.data
        });
      }
      
      // Appeler le callback avec les données
      if (callback) {
        callback(payload);
      }
    }
  });
}

/**
 * Vérifier si les notifications sont supportées et activées
 */
export function areNotificationsSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Obtenir le statut actuel de la permission
 */
export function getNotificationPermission() {
  if (!areNotificationsSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

/**
 * Désactiver les notifications pour cet appareil
 */
export async function disableNotifications(userId) {
  try {
    const tokenRef = doc(db, 'users', userId);
    await setDoc(tokenRef, {
      fcmTokens: [],
      notificationsEnabled: false
    }, { merge: true });
    
    console.log('🔕 Notifications désactivées');
    return true;
  } catch (error) {
    console.error('Erreur désactivation notifications:', error);
    return false;
  }
}
